import {
  Component,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { Export, ExportType } from 'depoto-core/src/entities'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { CoreService, NotificationService } from '../../../services'
import { TranslateService } from '@ngx-translate/core'
import { updateCachedListQueries } from '../../../services/query-cache.service'
import { QueryClient } from '@tanstack/angular-query-experimental'
import { withDateBoundary } from '../../../utils'

@Component({
  selector: 'modal-export-create',
  templateUrl: 'modal-export-create.component.html',
  styleUrls: ['modal-export-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalExportCreateComponent implements OnInit {
  @ViewChild('modal') childModal: ModalContainerComponent
  @Input() filters: any
  @Input() entity: string
  @Input() dataCyAttribute: string
  @Input() totalItems: number
  @Output() onResult: EventEmitter<any> = new EventEmitter()

  readonly maxExportItems = 50000

  queryClient = inject(QueryClient)
  type = ''
  types: ExportType[] = []
  note = ''
  // Voucher exports only: sent as `created`, which the server reads as the sale date for vouchers-sold,
  // the redemption date for vouchers-used and the voucher's creation date for the other voucher exports
  period = { left_date: '', right_date: '' }
  loading = false
  headerTitle: string
  filtersWithValues = {}
  constructor(
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.headerTitle = `exports.${this.entity}`
  }

  async getTypes() {
    this.loading = true
    // TODO CORE QUERY
    this.types = await this.core.services.export
      .getTypes(
        {},
        {
          id: null,
          name: null,
          format: null,
          group: null,
        }
      )
      .then(res => res.items || [])
    try {
      this.types = this.types.filter(t => t.group === this.entity)
      this.type = this.types[0].id
    } catch (e) {
      console.warn(e)
    }
    this.loading = false
  }

  isExportLimitExceeded(): boolean {
    return this.totalItems > this.maxExportItems
  }

  isVoucherExport(): boolean {
    return this.entity === 'vouchers'
  }

  periodLabelKey(bound: 'from' | 'to'): string {
    const meaning = this.type === 'vouchers-sold' ? 'sold' : this.type === 'vouchers-used' ? 'used' : 'created'
    return `export.voucherPeriod.${meaning}.${bound}`
  }

  async create(): Promise<void> {
    if (this.isExportLimitExceeded()) {
      alert(this.translateService.instant('export.max.items.exceeded'))
      return
    }
    const options: any = {}
    const entries = Object.entries(this.filters)
    for (const [key, value] of entries) {
      if (
        value !== '' &&
        value !== null &&
        key !== undefined &&
        value !== undefined &&
        value !== 'all' &&
        value !== 'none' &&
        key !== 'dateFrom' &&
        key !== 'dateTo' &&
        key !== 'dateExpedition' &&
        key !== 'dateBill' &&
        key !== 'dateTax' &&
        key !== 'validDate' &&
        key !== 'dateCreated' &&
        key !== 'dateDue'
      ) {
        if (
          (Array.isArray(value) && value.length < 1) ||
          (key === 'checkout' && value === 'withoutCheckout') ||
          (key === 'withoutCheckout' && !value)
        ) {
          options[key] = value
        } else {
          options[key] = value
        }
      } else if ((key === 'dateFrom' || key === 'dateTo') && value) {
        options.created = {
          ...(this.filters.dateFrom?.length && {
            left_date: withDateBoundary(this.filters.dateFrom, 'start'),
          }),
          ...(this.filters.dateTo?.length && {
            right_date: withDateBoundary(this.filters.dateTo, 'end'),
          }),
        }
      } else if (
        (key === 'dateExpedition' || key === 'expirationDate') &&
        (this.filters.dateExpedition.left_date?.length || this.filters.dateExpedition.right_date?.length)
      ) {
        options.dateExpedition = {
          ...(this.filters.dateExpedition.left_date?.length && {
            left_date: withDateBoundary(this.filters.dateExpedition.left_date, 'start'),
          }),
          ...(this.filters.dateExpedition.right_date?.length && {
            right_date: withDateBoundary(this.filters.dateExpedition.right_date, 'end'),
          }),
        }
      } else if (
        key === 'dateBill' &&
        (this.filters.dateBill.left_date?.length || this.filters.dateBill.right_date?.length)
      ) {
        options.dateBill = {
          ...(this.filters.dateBill.left_date?.length && {
            left_date: withDateBoundary(this.filters.dateBill.left_date, 'start'),
          }),
          ...(this.filters.dateBill.right_date?.length && {
            right_date: withDateBoundary(this.filters.dateBill.right_date, 'end'),
          }),
        }
      } else if (
        key === 'dateTax' &&
        (this.filters.dateTax.left_date?.length || this.filters.dateTax.right_date?.length)
      ) {
        options.dateTax = {
          ...(this.filters.dateTax.left_date?.length && {
            left_date: withDateBoundary(this.filters.dateTax.left_date, 'start'),
          }),
          ...(this.filters.dateTax.right_date?.length && {
            right_date: withDateBoundary(this.filters.dateTax.right_date, 'end'),
          }),
        }
      } else if (
        key === 'dateDue' &&
        (this.filters.dateDue.left_date?.length || this.filters.dateDue.right_date?.length)
      ) {
        options.dateDue = {
          ...(this.filters.dateDue.left_date?.length && {
            left_date: withDateBoundary(this.filters.dateDue.left_date, 'start'),
          }),
          ...(this.filters.dateDue.right_date?.length && {
            right_date: withDateBoundary(this.filters.dateDue.right_date, 'end'),
          }),
        }
      } else if (
        key === 'dateCreated' &&
        (this.filters.dateCreated.left_date?.length || this.filters.dateCreated.right_date?.length)
      ) {
        options.dateCreated = {
          ...(this.filters.dateCreated.left_date?.length && {
            left_date: withDateBoundary(this.filters.dateCreated.left_date, 'start'),
          }),
          ...(this.filters.dateCreated.right_date?.length && {
            right_date: withDateBoundary(this.filters.dateCreated.right_date, 'end'),
          }),
        }
      } else if (
        key === 'validDate' &&
        (this.filters.validDate.left_date?.length || this.filters.validDate.right_date?.length)
      ) {
        options.validDate = {
          ...(this.filters.validDate.left_date?.length && {
            left_date: withDateBoundary(this.filters.validDate.left_date, 'start'),
          }),
          ...(this.filters.validDate.right_date?.length && {
            right_date: withDateBoundary(this.filters.validDate.right_date, 'end'),
          }),
        }
      }
    }
    if (this.isVoucherExport() && (this.period.left_date || this.period.right_date)) {
      options.created = {
        ...(this.period.left_date && {
          left_date: withDateBoundary(this.period.left_date, 'start'),
        }),
        ...(this.period.right_date && {
          right_date: withDateBoundary(this.period.right_date, 'end'),
        }),
      }
    }
    const exportObject = new Export({
      type: { id: this.type },
      note: this.note,
      filter: JSON.stringify(options),
    })
    try {
      // TODO CORE MUTATION
      await this.core.services.export.create(exportObject, { id: null }).then(result => {
        this.removeExportListings()
        this.notificationService.success(this.translateService.instant('alert.export'))
        this.childModal.hideChildModal()
        this.onResult.emit(result)
      })
    } catch (err) {
      console.error(err)
    }
  }

  getFilters() {
    this.filtersWithValues = {}
    for (const [key, value] of Object.entries(this.filters)) {
      // olga's refucktoring: test this shit lol. surprise! not the same result
      // if ((val && !Array.isArray(val)) || typeof val === 'boolean' || (Array.isArray(val) && val.length)) {
      //   this.filtersWithValues[key] = val
      // }
      if (
        value !== undefined &&
        value !== null &&
        value !== 0 &&
        value !== '' &&
        ((Array.isArray(value) && value.length > 0) || !Array.isArray(value))
      ) {
        this.filtersWithValues[key] = value
      }
    }
    return
  }

  showChildModal(): void {
    this.period = { left_date: '', right_date: '' }
    this.getTypes()
    this.getFilters()
    this.childModal.showChildModal()
  }

  removeExportListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['exports'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }
}
